// Hand-maintained table of announced/approved residential rate changes. An
// entry owns its year outright in engine/projectMonthlyBills, ahead of both the
// STEO forecast and the selected view's escalator.
// `impact` is a STATE-AVERAGE judgment, not the utility's headline number — a
// utility serving half a state contributes roughly half its increase. Document
// the reasoning in `note` and keep `sourceUrl` pointing at the regulator or
// utility proceeding.
//
// TODO(bill-projection): v1 is a hard table per Nick 2026-08-18; transition to a
// live data source (regulatory feed) in a v2. Same data-owner + update-cadence
// requirement as data/content.ts.

export type KnownRateChange = {
  /** State-average YoY impact as a fraction (0.04 = +4%) */
  impact: number;
  /** Utility + proceeding + statewide-impact reasoning */
  note: string;
  sourceUrl: string;
  year: number;
};

// Researched 2026-08-18 (web sources, per-entry reasoning in PR #1758). Approved
// proceedings unless the note says pending. AZ (APS case undecided) and MA
// (6-month supply resets) were deliberately skipped as too speculative.
export const knownRateChanges: Record<string, KnownRateChange[]> = {
  CA: [
    {
      impact: 0.015,
      note: 'PG&E 2027–2030 rate case pending at the CPUC (~3.6% typical bill, ~40% of CA customers) — not yet approved',
      sourceUrl:
        'https://www.cpuc.ca.gov/news-and-updates/all-news/cpuc-to-hold-public-forums-on-pacific-gas-and-electric-2027-rate-case',
      year: 2027,
    },
  ],
  FL: [
    {
      impact: 0.01,
      note: 'FPL 2026–2029 rate settlement approved by the Florida PSC (~2% typical bill, FPL serves about half of FL)',
      sourceUrl: 'https://www.floridapsc.com/news-links/12299',
      year: 2026,
    },
    {
      impact: 0.01,
      note: 'FPL settlement year 2 — average annual bill increases capped near 2%',
      sourceUrl:
        'https://www.prnewswire.com/news-releases/fpl-rate-settlement-reduces-request-by-nearly-a-third-limits-average-annual-bill-increases-to-2-maintains-consumer-protections-302535029.html',
      year: 2027,
    },
  ],
  GA: [
    {
      impact: 0,
      note: 'Georgia Power base rates frozen through 2028 by the Georgia PSC',
      sourceUrl:
        'https://www.georgiapower.com/news-hub/press-releases/georgia-psc-approves-plan-to-freeze-base-rates-through-2028.html',
      year: 2026,
    },
    {
      impact: 0,
      note: 'Georgia Power base-rate freeze continues through 2028',
      sourceUrl:
        'https://www.georgiapower.com/news-hub/press-releases/georgia-psc-approves-plan-to-freeze-base-rates-through-2028.html',
      year: 2027,
    },
  ],
  IL: [
    {
      impact: 0.02,
      note: 'ComEd and Ameren Illinois delivery updates approved by the ICC, effective Jan 2026',
      sourceUrl: 'https://www.citizensutilityboard.org/blog/2026/01/19/cubs-report-on-2026-electric-rates/',
      year: 2026,
    },
  ],
  NC: [
    {
      impact: 0.04,
      note: 'Duke Energy Carolinas/Progress settlements (~5.7% residential, ~75% of NC) — NCUC vote pending',
      sourceUrl:
        'https://ncdoj.gov/duke-energy-gave-incorrect-residential-rate-increase-families-to-pay-9-3-more-not-6-8/',
      year: 2027,
    },
  ],
  NJ: [
    {
      impact: 0,
      note: '2026 BGS supply auction certified by the NJ BPU — statewide supply prices roughly flat',
      sourceUrl:
        'https://www.roi-nj.com/2026/02/17/industry/energy-utilities/njbpu-certifies-2026-electricity-auction-jcpl-customers-to-see-1-6-increase/',
      year: 2026,
    },
  ],
  NY: [
    {
      impact: 0.015,
      note: 'ConEd three-year rate plan approved by the NY PSC (+3.5% total bill, ~45% of NY customers)',
      sourceUrl: 'https://dps.ny.gov/news/psc-dramatically-reduces-con-edisons-rate-request-nearly-90-percent',
      year: 2026,
    },
    {
      impact: 0.015,
      note: 'ConEd rate plan year 2 (+3.2% total bill)',
      sourceUrl: 'https://dps.ny.gov/news/psc-dramatically-reduces-con-edisons-rate-request-nearly-90-percent',
      year: 2027,
    },
  ],
  OH: [
    {
      impact: 0.01,
      note: 'AEP Ohio distribution increase approved by PUCO, partly offset by FirstEnergy credits (ceiling-based estimate)',
      sourceUrl: 'https://puco.ohio.gov/news/aep-rate-case-2026',
      year: 2026,
    },
  ],
  PA: [
    {
      impact: 0.01,
      note: 'PPL distribution settlement approved by the PA PUC (+3.2% residential, ~25% of PA customers)',
      sourceUrl:
        'https://www.puc.pa.gov/press-release/2026/puc-issues-decision-in-ppl-electric-rate-proceeding-06042026',
      year: 2026,
    },
  ],
  TX: [
    {
      impact: 0.02,
      note: 'Oncor and CenterPoint delivery increases approved by the Texas PUC (~3% typical bill, ~65% of the market)',
      sourceUrl: 'https://www.oncor.com/content/oncorwww/us/en/home/rate-case.html',
      year: 2026,
    },
  ],
};
